<template>
  <div>
    <div class="investigation-summary__cols">
      <!-- Configuratie -->
      <div class="ds-card" data-qa="investigation-configuration-card">
        <div class="ds-card__header">Configuratie</div>
        <p v-if="!configuration" class="investigation-summary__note">
          Niet opgebouwd — de configuratie is niet volledig.
        </p>
        <template v-else>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Merk</span>
            <span class="ds-row__value">
              {{ configuration.commercialBrand }} ({{ configuration.brandCode }})
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Model / uitvoering</span>
            <span class="ds-row__value">
              {{ configuration.modelType }} {{ configuration.trimLine || '' }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Modeljaar</span>
            <span class="ds-row__value">{{ configuration.modelYear }}</span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Typecode</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--code">{{ configuration.typeCode }}</span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Pon Catalog Car</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--code">{{ configuration.ponCatalogCarId }}</span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">CoCa ID</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--code">{{ configuration.cocaId }}</span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Divisiecode</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--code">{{ configuration.divisionCode }}</span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Bouwbaar</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--dot" :class="tone(configuration.buildable)">
                {{ formatBoolean(configuration.buildable) }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Compleet</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--dot" :class="tone(configuration.complete)">
                {{ formatBoolean(configuration.complete) }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Opties</span>
            <span class="ds-row__value investigation-summary__value--with-action">
              {{ optionCountLabel }}
              <button
                v-if="equipment.length"
                type="button"
                class="ds-info-button"
                title="Toon alle opties uit de GET_STANDARD_OPTIONS call"
                aria-label="Toon alle opties"
                data-qa="investigation-options-button"
                @click="showOptions = true"
              >
                <ds-icon name="info" />
              </button>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Afbeeldingen</span>
            <span class="ds-row__value">
              <span
                class="ds-chip ds-chip--dot"
                :class="configuration.hasImages ? 'ds-chip--positive' : 'ds-chip--warning'"
              >
                {{ configuration.hasImages ? 'Aanwezig' : 'Ontbreken' }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">WLTP CO₂</span>
            <span class="ds-row__value">
              {{ configuration.wltpEmission != null ? `${configuration.wltpEmission} g/km` : '–' }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Orderkey ({{ configuration.orderKeyFeatureCount }})</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="configuration.orderKeyFeatures"
                :descriptions="descriptions"
              />
            </span>
          </div>
        </template>
      </div>

      <!-- Prijzen -->
      <div class="ds-card" data-qa="investigation-price-card">
        <div class="ds-card__header">Prijzen (Pricemaster)</div>
        <p v-if="!prices || !prices.called" class="investigation-summary__note">
          Pricemaster is niet aangeroepen — er zijn geen prijzen berekend.
        </p>
        <template v-else>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Status</span>
            <span class="ds-row__value">
              <span class="ds-chip ds-chip--dot" :class="tone(prices.statusErrorCode === 0)">
                {{ priceStatusLabel }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Prijspeildatum</span>
            <span class="ds-row__value">{{ formatDate(prices.calculationDate) }}</span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Basisprijs excl.</span>
            <span class="ds-row__value investigation-summary__amount">
              {{ formatEuro(prices.netPriceExcl) }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Opties excl.</span>
            <span class="ds-row__value investigation-summary__amount">
              {{ formatEuro(prices.optionsTotalExcl) }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Totaal excl. BTW</span>
            <span class="ds-row__value investigation-summary__amount">
              {{ formatEuro(prices.totalPriceExcl) }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">{{ bpmLabel }}</span>
            <span class="ds-row__value investigation-summary__amount">
              {{ prices.bpmApplicable ? formatEuro(prices.bpm) : 'Niet van toepassing' }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Totaal incl. {{ prices.btwPercentage }}% BTW</span>
            <span class="ds-row__value investigation-summary__amount">
              {{ formatEuro(prices.totalPriceIncl) }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">CO₂ bepaald</span>
            <span class="ds-row__value">
              <span
                class="ds-chip ds-chip--dot"
                :class="prices.co2Determined ? 'ds-chip--positive' : 'ds-chip--warning'"
              >
                {{ formatBoolean(prices.co2Determined) }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Opties zonder prijs</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="prices.optionsWithoutPrice"
                :descriptions="descriptions"
              />
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Opties met nulprijs</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="prices.zeroPriceOptions"
                :zero="prices.zeroPriceOptions || []"
                :descriptions="descriptions"
              />
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Optie-foutcodes</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="prices.optionErrorCodes"
                :descriptions="descriptions"
              />
            </span>
          </div>
          <div v-if="extensionOptionCodes.length" class="ds-row ds-row--2col">
            <span class="ds-row__label">Extensie-opties met prijs</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="extensionOptionCodes"
                :descriptions="descriptions"
              />
            </span>
          </div>
        </template>
      </div>

      <!-- Leverdatum -->
      <div class="ds-card" data-qa="investigation-delivery-card">
        <div class="ds-card__header">Leverdatum (LIB)</div>
        <p v-if="!delivery || !delivery.called" class="investigation-summary__note">
          Niet aangeroepen — zonder complete configuratie is er geen leverdatum.
        </p>
        <template v-else>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Leverdatum bekend</span>
            <span class="ds-row__value">
              <span
                class="ds-chip ds-chip--dot"
                :class="delivery.hasDeliveryDate ? 'ds-chip--positive' : 'ds-chip--warning'"
              >
                {{ formatBoolean(delivery.hasDeliveryDate) }}
              </span>
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Productieweek</span>
            <span class="ds-row__value">{{ formatWeek(delivery.expectedProductionWeek) }}</span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Transportweek</span>
            <span class="ds-row__value">{{ formatWeek(delivery.expectedTransportWeek) }}</span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Modeltype</span>
            <span class="ds-row__value">
              {{ delivery.modelType }} / {{ delivery.modelYear }} /
              {{ delivery.modelExecutionCode }}
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Kleur</span>
            <span class="ds-row__value">
              <investigation-code-chips :codes="colorCodes" :descriptions="descriptions" />
            </span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Klantorder</span>
            <span class="ds-row__value">{{ formatBoolean(delivery.customerOrder) }}</span>
          </div>
          <div class="ds-row ds-row--2col">
            <span class="ds-row__label">Opties</span>
            <span class="ds-row__value">
              <investigation-code-chips
                :codes="delivery.optionCodes"
                :descriptions="descriptions"
              />
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- productDataKeyProbe: the same marketing code asked again under the brand's backup product
         data key, to tell "this code is broken" apart from "this key is". One attempt, one card. -->
    <div
      v-if="probe"
      class="ds-card investigation-summary__probe"
      data-qa="investigation-probe-card"
    >
      <div class="ds-card__header">Product data key probe</div>
      <div class="ds-row ds-row--2col">
        <span class="ds-row__label">Reserve product data key</span>
        <span class="ds-row__value ds-code">{{ probe.productDataKey }}</span>
      </div>
      <div class="ds-row ds-row--2col">
        <span class="ds-row__label">Code gevonden</span>
        <span class="ds-row__value">
          <span class="ds-chip ds-chip--dot" :class="tone(probe.found)">
            {{ formatBoolean(probe.found) }}
          </span>
        </span>
      </div>
      <div class="ds-row ds-row--2col">
        <span class="ds-row__label">Verwijderde opties</span>
        <span class="ds-row__value">
          <investigation-code-chips
            v-if="probeRemovedOptions.length"
            :codes="probeRemovedOptions"
            :descriptions="descriptions"
          />
          <span v-else>Geen</span>
        </span>
      </div>
      <div v-if="probe.errorMessage" class="ds-row ds-row--2col">
        <span class="ds-row__label">Fout</span>
        <span class="ds-row__value">{{ probe.errorType }}: {{ probe.errorMessage }}</span>
      </div>
    </div>

    <investigation-options-dialog
      v-if="showOptions"
      :options="equipment"
      @close="showOptions = false"
    />
  </div>
</template>

<script>
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import InvestigationCodeChips from './InvestigationCodeChips.vue'
import InvestigationOptionsDialog from './InvestigationOptionsDialog.vue'
import { formatBoolean, formatDate, formatEuro, formatWeek } from './investigationFormat'

// The three summaries the endpoint builds — configuration, Pricemaster prices
// and the LIB delivery date — plus the product data key probe.
//
// Option descriptions are not part of the summaries: they only exist in the
// GET_STANDARD_OPTIONS response body, so they are read back out of the call log
// and used to annotate the bare codes the summaries hand back.

export default {
  name: 'InvestigationSummary',
  components: { DsIcon, InvestigationCodeChips, InvestigationOptionsDialog },
  props: {
    result: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { showOptions: false }
  },
  computed: {
    configuration() {
      return this.result.configurationSummary
    },
    prices() {
      return this.result.priceSummary
    },
    delivery() {
      return this.result.deliveryDateSummary
    },
    probe() {
      return this.result.productDataKeyProbe || null
    },
    probeRemovedOptions() {
      return this.probe?.removedOptions || []
    },
    // Without a classification an entry is not an option (VERSION, MARKET,
    // BASETYPE, …), so those are dropped.
    equipment() {
      const call = (this.result.calls || []).find(
        (item) => item.stage === 'GET_STANDARD_OPTIONS' && item.responseBody
      )
      if (!call) return []

      try {
        const options = JSON.parse(call.responseBody)?.vehicle_configuration?.options
        return Array.isArray(options) ? options.filter((option) => option?.classification) : []
      } catch {
        return []
      }
    },
    // A loose option code is matched on the full code, on the part after the
    // ':' (WBT) and on the part before it (AAA).
    descriptions() {
      const map = {}
      this.equipment.forEach((option) => {
        if (!option.code || !option.description) return
        const parts = String(option.code).split(':')
        ;[option.code, parts[1], parts[0]].forEach((key) => {
          if (key && !map[key]) map[key] = option.description
        })
      })
      return map
    },
    optionCountLabel() {
      const chosen = `${this.configuration.optionCount} gekozen`
      if (!this.equipment.length) return chosen
      const standard = Math.max(0, this.equipment.length - this.configuration.optionCount)
      return `${chosen} · ${standard} standaard`
    },
    priceStatusLabel() {
      const { statusErrorCode, statusErrorText } = this.prices
      // PriceMaster leaves both out when it never answered, which used to render as
      // "undefined (undefined)".
      if (statusErrorText == null && statusErrorCode == null) return 'Onbekend'
      if (statusErrorCode == null || statusErrorCode === 0) return statusErrorText || 'OK'
      return `${statusErrorText || 'Fout'} (${statusErrorCode})`
    },
    bpmLabel() {
      return this.prices.bpmCode ? `BPM (${this.prices.bpmCode})` : 'BPM'
    },
    // The endpoint hands these back either as codes or as objects carrying one.
    extensionOptionCodes() {
      return (this.prices?.extensionOptionsWithPrices || []).map((option) =>
        typeof option === 'string' ? option : option.code || option.prCode || JSON.stringify(option)
      )
    },
    colorCodes() {
      return this.delivery?.color ? [this.delivery.color] : []
    },
  },
  methods: {
    formatBoolean,
    formatDate,
    formatEuro,
    formatWeek,
    tone(value) {
      return value ? 'ds-chip--positive' : 'ds-chip--danger'
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The cards, rows, chips and table come from the design system
// (styles/ds/). See styles/ds/README.md before adding anything here.

.investigation-summary {
  &__cols {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
    gap: 14px;
    align-items: start;
  }

  // A summary that could not be built says so instead of showing empty rows.
  &__note {
    margin: 0;
    padding: 18px 22px;
    font-size: var(--ds-font-size-base);
    font-style: italic;
    color: var(--ds-color-text-subtle);
  }

  &__value--with-action {
    display: flex;
    align-items: center;
    gap: var(--ds-space-2);
  }

  &__amount {
    font-variant-numeric: tabular-nums;
  }

  &__probe {
    margin-top: 14px;

    // The card already draws the border and shadow around the table.
    .ds-table {
      border: 0;
      border-radius: 0;
      box-shadow: none;
    }

    .ds-table__scroll {
      max-height: 420px;
    }
  }
}
</style>
